import qs from "qs";

import { GetDataWithErrors } from "./Functions/SettingData";

// API module for Asset Management -> Network tab.
// Endpoints and payload shapes: network-tab-spec.md section 2.
//
// GetDataWithErrors, not GetData: this API returns per-field errors in
// `data.errors` on a 400/409, and GetData copies only responseStatus.code and
// .msg on a 400 (Api/Functions/SettingData.js) -- so the form could not
// highlight the offending fields. There are no axios interceptors in this
// codebase, so bypassing the shared handling entirely would silently lose it.
//
// What it delegates, precisely:
//   400 / 409 / 422  -- body returned untouched, for the per-field errors
//   401 / 403        -- delegated to GetData: the token refresh and the
//                       subscription-expiry logout. Safe to re-issue, because
//                       the server rejected the request before doing anything.
//   anything else    -- delegated only for idempotent methods. A POST, PUT or
//                       DELETE that failed with a 500 or no response at all may
//                       ALREADY have been applied, so it is reported rather
//                       than retried.
//
// One gap worth knowing: the success path returns the body verbatim and does
// not run callTheAPI, so a 200 carrying an in-body 401 gets no token cleanup.
// These endpoints answer with real HTTP statuses, so that shape does not arise
// here -- but a future endpoint that returns one would not be handled.

// Search, sort and filter params shared by the list and the export.
//
// Built in ONE place so a download cannot narrow differently from the table it
// was started from -- the backend enforces the same pairing (one schema mixin,
// one query builder), and this is the client half of that contract.
//
// Only defined params are sent: both schemas use unknown=RAISE, so an explicit
// `search=undefined` would serialise to an empty string and be rejected rather
// than treated as "no search".
export const buildNetworkEntityParams = ({
    entityTypeCode,
    search,
    searchBy,
    sortBy,
    sortOrder,
    orgLocationMasterIDs,
    updatedFrom,
    updatedTo,
}) => {
    const params = { entityTypeCode };
    if (search) params.search = search;
    if (searchBy) params.searchBy = searchBy;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    // Comma separated, not repeated keys: the backend flattens request.args to
    // a dict, which keeps only the first value of a repeated key -- a filter
    // that looked applied but matched on one selection.
    if (orgLocationMasterIDs?.length) {
        params.orgLocationMasterIDs = orgLocationMasterIDs.join(",");
    }
    if (updatedFrom) params.updatedFrom = updatedFrom;
    if (updatedTo) params.updatedTo = updatedTo;
    return params;
};

// GET /api/network-entities
export const getNetworkEntities = async ({ page = 1, limit = 25, ...rest }) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/network-entities?${qs.stringify({
            ...buildNetworkEntityParams(rest),
            page,
            limit,
        })}`,
    });

// The selection half of the export query -- the Inventory contract:
// `resourceIDs` (network entity ids) and `selectAll`. The server ignores
// resourceIDs when selectAll is true and narrows to them otherwise.
//
// Comma separated for the same reason as orgLocationMasterIDs above (the
// backend flattens repeated keys to the first value). An empty selection sends
// neither key, which means the whole filtered set -- and keeps the request
// valid for an older backend whose export schema rejects unknown fields.
export const buildNetworkSelectionParams = ({ resourceIDs, selectAll } = {}) => {
    if (selectAll) return { selectAll: "true" };
    const ids = (Array.isArray(resourceIDs) ? resourceIDs : []).filter(
        (id) => id !== null && id !== undefined && id !== ""
    );
    return ids.length ? { selectAll: "false", resourceIDs: ids.join(",") } : {};
};

// GET /api/network-entities/export -- every matching row, unpaginated, for the
// CSV. Takes no page or limit on purpose: an export is the whole filtered set,
// narrowed server-side to the selection when there is one.
export const exportNetworkEntities = async ({ resourceIDs, selectAll, ...args } = {}) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/network-entities/export?${qs.stringify({
            ...buildNetworkEntityParams(args),
            ...buildNetworkSelectionParams({ resourceIDs, selectAll }),
        })}`,
    });

// GET /api/network-entities/<id>
//
// The detail view reads through this rather than reusing the row it was opened
// from: a list row carries only the columns the table shows, and the detail
// endpoint is where linked records (a customer's batteries and their loan IDs)
// will arrive when they exist.
export const getNetworkEntity = async (entityID) =>
    GetDataWithErrors({
        method: "get",
        url: `/api/network-entities/${entityID}`,
    });

// GET /api/network-entities/search-suggestions
//
// Signature matches what DeviceSearchInput calls its handler with
// (term, columnName, limit), so it drops straight into searchSuggestionsHandler.
// Values are scoped to the active entity type -- typing under Vendors must not
// offer a customer's name.
export const getNetworkEntitySearchSuggestions = async (
    entityTypeCode,
    columnSearch,
    columnName,
    columnLimit = 5
) => {
    const res = await GetDataWithErrors({
        method: "get",
        url: `/api/network-entities/search-suggestions?${qs.stringify({
            entityTypeCode,
            columnName,
            columnSearch,
            columnLimit,
        })}`,
    });

    // Reshaped, not returned raw. GetDataWithErrors hands back the response
    // body as-is -- {responseStatus, data} -- but DeviceSearchInput reads
    // `res.response.data` (DeviceSearchInput.jsx:54), the shape GetData
    // produces. Returning the envelope untouched yields an always-empty
    // dropdown with no error anywhere.
    const values = Array.isArray(res?.data) ? res.data : [];
    return {
        responseStatus: res?.responseStatus || { code: 0, msg: null },
        response: { data: values },
    };
};

// POST /api/network-entities/email -- mails the CSV.
//
// `rows` are the rows the Download button would produce, already formatted and
// keyed by headerLabel. The server addresses and sends them rather than
// rebuilding the file: a second CSV builder there would be free to drift from
// the one the user can see on screen.
export const emailNetworkEntities = async ({
    sendTo,
    sendCc,
    subject,
    body,
    rows,
    fileName,
}) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/network-entities/email",
        headers: { "Content-Type": "application/json" },
        data: { sendTo, sendCc: sendCc || [], subject, body, rows, fileName },
    });

// POST /api/network-entities
// `payload` is flat: entityTypeCode plus the type's declared properties. The
// backend splits core columns from custom attributes; the client never needs to
// know which is which.
export const createNetworkEntity = async (payload) =>
    GetDataWithErrors({
        method: "post",
        url: "/api/network-entities",
        headers: { "Content-Type": "application/json" },
        data: payload,
    });

// PUT /api/network-entities/<id>
export const updateNetworkEntity = async (entityID, payload) =>
    GetDataWithErrors({
        method: "put",
        url: `/api/network-entities/${entityID}`,
        headers: { "Content-Type": "application/json" },
        data: payload,
    });

// DELETE /api/network-entities/<id> -- soft delete. Approver role only; a user
// without it gets 403 and the UI hides the action rather than offering it.
export const deleteNetworkEntity = async (entityID) =>
    GetDataWithErrors({ method: "delete", url: `/api/network-entities/${entityID}` });
